// Helpers d'aplatissement de la hiérarchie cfg.props[].logs[].chambres[].
//
// Pourquoi : la sous-location FBZ (p6 "Maison Zen") expose un modèle hiérarchique
// "1 log parent + N chambres" alors que les autres proprios ont des logs feuilles
// directs. Sans helper, chaque composant qui veut "tous les logements visibles"
// doit ré-implémenter la fusion log/chambres + propager propId/propNom — ce qui
// produit silencieusement des bugs (ex. "Cocon Naturel × 3" en cas de doublons
// dans cfg).
//
// Convention de retour : objets enrichis { ...log_ou_chambre, propId, propNom,
// parentLog? }. parentLog présent uniquement pour les chambres.
//
// Filet de sécurité : dédup par id (anti-bug "x3" historique). Si jamais cfg
// contient des doublons (incident migration Beds24 / saisie UI), le helper
// retourne 1 entrée par id même si cfg en référence plusieurs.

export function getAllLogsFlat(cfg) {
  const seen = new Set()
  const result = []
  for (const p of (cfg?.props || [])) {
    if (!p) continue
    for (const log of (p.logs || [])) {
      if (!log) continue
      const hasChambres = (log.chambres?.length || 0) > 0
      if (hasChambres) {
        // Sous-loc : on expose les chambres feuilles, pas le log parent.
        // (Le log parent reste accessible via l.parentLog pour les composants
        // qui ont besoin du contexte — ex. SousLocationView pour le loyer.)
        for (const ch of log.chambres) {
          if (!ch) continue
          if (seen.has(ch.id)) continue
          seen.add(ch.id)
          result.push({ ...ch, parentLog: log, propId: p.id, propNom: p.nom })
        }
      } else {
        if (seen.has(log.id)) continue
        seen.add(log.id)
        result.push({ ...log, propId: p.id, propNom: p.nom })
      }
    }
  }
  return result
}

// Résolution d'un logement par id côté client. Aligné sur la fonction CJS
// findLogementInCfg() de lib/messages-engine.js : retourne le log feuille avec
// parentLog attaché si c'est une chambre sous-loc, ou null si introuvable.
//
// Utile pour :
//   - le résolveur de variables côté preview (renderTemplate dans MessageEditor
//     / LivretTab) qui doit voir parentLog pour faire le fallback Livret ;
//   - les composants UI qui ont juste un logementId (string) et veulent
//     l'objet log complet.
//
// Implémentation = wrapper sur getAllLogsFlat (qui dédup déjà par id et attache
// parentLog) plutôt qu'une 2nd boucle pour rester DRY.
export function findLogementInCfg(cfg, logementId) {
  if (!logementId) return null
  return getAllLogsFlat(cfg).find(l => l.id === logementId) || null
}

/**
 * Nom affichable d'un logement à partir de son id, CHAMBRES COMPRISES.
 *
 * Pourquoi ce helper : plusieurs écrans résolvaient le nom avec un
 * `cfg.props.flatMap(p => p.logs)` qui ne descend PAS dans `log.chambres` — une
 * ligne portant `lc1` affichait alors l'identifiant brut « lc1 » au lieu de
 * « Cocon Naturel » (constat audit, onglet Incidents). Chaque écran
 * ré-implémentait sa propre résolution et elles divergeaient.
 *
 * Contrairement à `getAllLogsFlat`, ce résolveur voit AUSSI les logements
 * PARENTS (l8 « Maison Zen ») : getAllLogsFlat n'expose que les feuilles
 * vendables, mais une donnée historique peut porter l'id du parent — mieux vaut
 * afficher son nom que son id.
 *
 * @param {object|null} cfg — config (cfg.props[].logs[].chambres[]).
 * @param {string} logementId
 * @returns {string} nom du logement/chambre, ou l'id tel quel si introuvable
 *   (jamais vide, jamais undefined : c'est un libellé d'écran).
 */
export function resolveLogementNom(cfg, logementId) {
  if (!logementId) return logementId
  for (const p of (cfg?.props || [])) {
    for (const log of (p.logs || [])) {
      if (log.id === logementId) return log.nom || log.id
      for (const ch of (log.chambres || [])) {
        if (ch.id === logementId) return ch.nom || ch.id
      }
    }
  }
  return logementId
}

// ── PR #169a-bis (ADR-105) ─────────────────────────────────────────────
// Helpers `pricing_blocks` co-localisés ici car partagés par :
//   - ResaV2Modal > InfosTab (édition kind=pricing_block)
//   - _PricingBlockCreateBody.jsx (création, indirect via callers)
//   - Mapper mapBlockToReservationShape (shape unifié reservations)
// ──────────────────────────────────────────────────────────────────────

/**
 * Pré-fill values depuis un pricing_block existant pour <GuestFieldsForm>.
 * NULL → "" (controlled inputs React). Migré depuis PricingBlockGuestEditModal
 * (PR #169a v1.4) lors PR #169a-bis pour co-localisation avec findLogementInCfg.
 *
 * Whitelist 13 champs voyageur + total_amount (cohérent serveur
 * PRICING_BLOCK_GUEST_WHITELIST, cf. api/beds24-mapping.js).
 *
 * @param {object|null} block — pricing_block row Supabase
 * @returns {object} values prêts pour <GuestFieldsForm>
 */
export function blockToGuestFields(block) {
  if (!block) return {}
  return {
    guest_first_name: block.guest_first_name || "",
    guest_last_name: block.guest_last_name || "",
    guest_email: block.guest_email || "",
    guest_phone: block.guest_phone || "",
    guest_address: block.guest_address || "",
    guest_city: block.guest_city || "",
    guest_postal_code: block.guest_postal_code || "",
    guest_country: block.guest_country || "",
    guest_language: block.guest_language || "",
    guest_count: block.guest_count ?? "",
    children_count: block.children_count ?? "",
    notes: block.notes || "",
    total_amount: block.total_amount ?? "",
  }
}

/**
 * Convertit une row pricing_block vers shape compat reservations.
 * Permet à InfosTab/FactureMiniTab/ContratTab de lire `r.guest_first_name`
 * etc. de manière uniforme sans switch sur kind. Q-D10 décision Dimitri :
 * `source = 'direct'` (pas un synthétique 'block_manual') — cohérent
 * sémantique "résa interne FBZ = direct booking-like".
 *
 * Champs absents mappés à `null` (jamais undefined) :
 *   - booking_date, arrival_time, departure_time (accueil autonome v1)
 *   - cleaning_fee, city_tax, ota_commission (résa interne pas d'OTA)
 *   - payment_received_at, caution_received_at (Phase Stripe future)
 *   - beds24_booking_id, raw_payload (résa interne jamais Beds24)
 *
 * Champs spécifiques pricing_blocks préfixés `_*` (LPR169a-bis-4) :
 *   - _reason, _active, _beds24_pushed, _released_at, _released_by
 *   Lus en lecture seule par FooterActions (release/push buttons).
 *
 * @param {object|null} block — pricing_block row
 * @returns {object|null} reservation-shape ou null si block null
 */
export function mapBlockToReservationShape(block) {
  if (!block) return null
  return {
    // Identifiants
    id: block.id,
    logement_id: block.logement_id,
    // Voyageur (12 champs communs reservations ↔ pricing_blocks)
    guest_first_name: block.guest_first_name,
    guest_last_name: block.guest_last_name,
    guest_email: block.guest_email,
    guest_phone: block.guest_phone,
    guest_address: block.guest_address,
    guest_city: block.guest_city,
    guest_postal_code: block.guest_postal_code,
    guest_country: block.guest_country,
    guest_language: block.guest_language,
    guest_count: block.guest_count,
    children_count: block.children_count,
    notes: block.notes,
    // Dates
    start_date: block.start_date,
    end_date: block.end_date,
    booking_date: block.created_at || null,
    arrival_time: null,
    departure_time: null,
    // Montant — cleaning_fee/city_tax/ota_commission null (mapped à 0 dans
    // facture via `Number(null)||0`, MPR169a-bis-1)
    total_amount: block.total_amount,
    cleaning_fee: null,
    city_tax: null,
    ota_commission: null,
    // Statut synthétique — Q-D10 : source='direct' (pas 'block_manual')
    source: 'direct',
    status: block.active === false ? 'cancelled' : 'confirmed',
    // Stripe (legacy markers — toujours absents pour pricing_blocks, pas de
    // colonnes payment_received_at/caution_received_at sur cette table).
    payment_received_at: null,
    caution_received_at: null,
    // Phase 2 Stripe (C1 migration 20260526000001) — 6 colonnes Stripe
    // ajoutées sur pricing_blocks. Propagation explicite pour PaiementsTab
    // (C8). On préserve null-safe pour blocs créés avant migration.
    caution_payment_intent_id: block.caution_payment_intent_id || null,
    caution_status: block.caution_status || null,
    caution_amount_eur: block.caution_amount_eur != null ? Number(block.caution_amount_eur) : null,
    paiement_sejour_intent_id: block.paiement_sejour_intent_id || null,
    paiement_sejour_status: block.paiement_sejour_status || null,
    paid_at: block.paid_at || null,
    // Phase 2 Stripe (C12 migration 20260527000001 ADR-109) — 6 colonnes
    // Checkout Sessions (pivot Stripe-hosted). Propagation explicite pour
    // CheckoutLinkBox (PaiementsTab) sur résa direct interne FBZ.
    // BUG-CRITIQUE-1 silent-fail pricing_blocks (pessimist QA C13).
    caution_checkout_session_id: block.caution_checkout_session_id || null,
    caution_checkout_url: block.caution_checkout_url || null,
    caution_checkout_expires_at: block.caution_checkout_expires_at || null,
    paiement_sejour_checkout_session_id: block.paiement_sejour_checkout_session_id || null,
    paiement_sejour_checkout_url: block.paiement_sejour_checkout_url || null,
    paiement_sejour_checkout_expires_at: block.paiement_sejour_checkout_expires_at || null,
    // Phase 2 Stripe (C20 PR #189 ADR-109 amend) — 2 colonnes Embedded
    // client_secret_partial (audit fragment 8+4 chars). Pattern 12 propagation
    // OBLIGATOIRE (cf. plan §11 1.).
    caution_checkout_client_secret_partial:
      block.caution_checkout_client_secret_partial || null,
    paiement_sejour_checkout_client_secret_partial:
      block.paiement_sejour_checkout_client_secret_partial || null,
    // Beds24 — absent par design (ADR-104 : résa interne jamais poussée Beds24)
    beds24_booking_id: null,
    raw_payload: null,
    // Spécifique pricing_blocks (LPR169a-bis-4) — préfixe `_` pour signaler
    // shape étendue. Lus uniquement par FooterActions + InfosTab détails.
    _reason: block.reason,
    _active: block.active,
    _beds24_pushed: block.beds24_pushed,
    _released_at: block.released_at,
    _released_by: block.released_by,
    // Lot annulation résa payée (2e accès calendrier) — marqueur provenance :
    // 'checkout' = bloc créé par le parcours direct-booking (demande liée dans
    // direct_booking_requests) → conditionne l'affichage du bouton « Annuler
    // la réservation » dans PaiementsTabBody SANS requête supplémentaire.
    _created_by: block.created_by || null,
  }
}
